import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { AutocompleteOption } from '../../../types';

type AutocompleteKeyDownProps = {
    options: AutocompleteOption[];
    activeIndex: number;
    showDefaultOption: boolean;
    text: string;
    showDefaultOptionOnFocus: boolean;
    setSelected: (index: number) => void;
    clearHovered: () => void;
    setOptions: (opts: AutocompleteOption[]) => void;
    setNoResults: (val: boolean) => void;
    setShowDropdown: (open: boolean) => void;
    selectCurrentOption: () => void;
};

export const useAutocompleteKeyDown = ({
    options,
    activeIndex,
    showDefaultOption,
    text,
    showDefaultOptionOnFocus,
    setSelected,
    clearHovered,
    setOptions,
    setNoResults,
    setShowDropdown,
    selectCurrentOption,
}: AutocompleteKeyDownProps) => {
    const activeIndexRef = useRef(activeIndex);
    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    return useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            const key = e.key;
            const total = options.length + (showDefaultOption ? 1 : 0);
            const current = activeIndexRef.current;
            const hasAny = total > 0;
            const hasSelection = current !== -1;

            if (key === 'ArrowDown' && text.length === 0 && showDefaultOptionOnFocus) {
                setShowDropdown(true);
                setSelected(0);
                clearHovered();
                e.preventDefault();
                return;
            }

            switch (key) {
                case 'ArrowDown': {
                    if (!hasAny) return;
                    const next = (current + 1 + total) % total;
                    setSelected(next);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'ArrowUp': {
                    if (!hasAny) return;
                    const prev = (current - 1 + total) % total;
                    setSelected(prev);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'Home': {
                    if (!hasAny) return;
                    setSelected(0);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'End': {
                    if (!hasAny) return;
                    setSelected(total - 1);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'Enter':
                case 'Tab': {
                    if (hasSelection) selectCurrentOption();
                    if (key === 'Enter') e.preventDefault();
                    return;
                }

                case 'Escape': {
                    setOptions([]);
                    setSelected(-1);
                    clearHovered();
                    setNoResults(false);
                    return;
                }

                default:
                    return;
            }
        },
        [
            options.length,
            showDefaultOption,
            text,
            showDefaultOptionOnFocus,
            setSelected,
            clearHovered,
            setOptions,
            setNoResults,
            setShowDropdown,
            selectCurrentOption,
        ],
    );
};
